/**
 * Generic "does any field on this record match the query" matcher for
 * global search (see SearchContext.tsx).
 *
 * Global search used to be a hand-picked list of `field?.toLowerCase()
 * .includes(q)` chains, one per entity block. That doesn't scale: every new
 * column needs a matching new line here that nobody remembers to add, which
 * is exactly how asoebi orders ended up matching only their AOS-xxx id (not
 * the guest's name, phone, notes, or the fabric/bundle they ordered) and
 * courier tracking numbers ended up matching nothing at all anywhere in the
 * app. This scans a record's own fields instead, so a new column is
 * searchable the moment it exists on the object global search already
 * loaded — no second edit required.
 *
 * What's deliberately excluded, and why — a person's search intent is a
 * name, a number they were quoted or handed, a reference, a status word,
 * free text they wrote. It is never:
 *
 *   - a raw database id or foreign key (`id`, `guest_id`, `venueId`, ...).
 *     These are internal wiring, not something a person recalls or types —
 *     the human-facing identifier is the formatted display id (AOS-104,
 *     VD-22), which callers merge into the object under a plain key like
 *     `displayId` that doesn't match the id-suffix pattern below.
 *   - a boolean flag (`archived`, `is_active`, `published_to_shop`) — no one
 *     types "true" or "false" into a search box.
 *   - a raw ISO timestamp-with-time (`2026-09-01T14:32:00.000Z`) — nobody
 *     recalls or types that string. Detected by VALUE shape, not key name,
 *     so a plain calendar date (`event.date`, `order_date` — just
 *     `"2026-09-01"`, no time component) is NOT suppressed; those were
 *     already searchable by hand before this file existed and a person
 *     plausibly does type a date.
 *   - an asset/binary reference (`image`, `photo`, `avatar`, `logo`, any
 *     `*_url`) — not human-typeable content, just a pointer to a file.
 *   - a secret-shaped field (`password`, `token`, `secret`, `api_key`, any
 *     `*_hash`) — defensive; none of these should ever reach the client,
 *     but they must never be indexed if one slips through.
 *   - a monetary amount (`price`, `amount`, `unit_cost_ngn`, `total_price`,
 *     `freight_amount`, anything with `ngn`/`cents` in its name). This
 *     mirrors how every hand-written block already behaved — none of them
 *     ever matched on an amount, only on names/references/notes/status —
 *     and it avoids a subtler problem: several of these entities are
 *     financial-gated (a financially-blind user's fetch omits the amount
 *     from what's DISPLAYED), but the raw numeric field is still sitting on
 *     the object in memory. Matching on it would let that user discover a
 *     hidden number exists near what they typed, even though they can never
 *     see the number itself — a leak through match behavior a plain grep of
 *     the rendered UI wouldn't reveal. Names/notes/references stay fully
 *     searchable for everyone; only the number itself is excluded.
 *
 * Nested objects and arrays are followed up to a few levels deep (default
 * 3 — covers the deepest real nesting today, budget category -> vendor line
 * -> payment), applying the same exclusions at every level.
 */

const MONEY_KEY_TOKENS = ['amount', 'price', 'cost', 'ngn', 'cents'];

/** Case-insensitive id-suffix check that only fires on an actual id-shaped
 *  key (`id`, `guest_id`, `categoryId`) — not on an unrelated word that
 *  happens to end in the same two letters (e.g. `paid`, `void`), which a
 *  blanket lowercase "ends with id" check would wrongly catch. */
function isIdKey(key: string): boolean {
  return key.toLowerCase() === 'id' || /_id$/i.test(key) || /Id$/.test(key);
}

function isNoiseKey(key: string): boolean {
  const k = key.toLowerCase();
  if (isIdKey(key)) return true;
  // party_of is a foreign key (guests.id of the guest this one is a +1 of)
  // that doesn't happen to match the `_id`/`Id` naming convention above —
  // same "not something a person types" reasoning as isIdKey.
  if (k === 'party_of') return true;
  if (k === 'image' || k === 'photo' || k === 'avatar' || k === 'logo') return true;
  if (k === 'url' || k.endsWith('_url')) return true;
  if (k === 'password' || k === 'token' || k === 'secret' || k === 'api_key') return true;
  if (k.endsWith('_hash')) return true;
  if (MONEY_KEY_TOKENS.some(t => k.includes(t))) return true;
  return false;
}

/** A full ISO-8601 timestamp with a time component — the noise case.
 *  A bare calendar date ("2026-09-01") does not match this and stays
 *  searchable. */
const ISO_TIMESTAMP_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function valueMatches(value: unknown, q: string, depth: number): boolean {
  if (value == null) return false;
  if (typeof value === 'boolean') return false;
  if (typeof value === 'string') {
    if (ISO_TIMESTAMP_RE.test(value)) return false;
    return value.toLowerCase().includes(q);
  }
  if (typeof value === 'number') {
    return String(value).toLowerCase().includes(q);
  }
  if (depth <= 0) return false;
  if (Array.isArray(value)) {
    return value.some(v => valueMatches(v, q, depth - 1));
  }
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).some(
      ([k, v]) => !isNoiseKey(k) && valueMatches(v, q, depth - 1)
    );
  }
  return false;
}

/**
 * Match a (already-lowercased, trimmed) search query against every
 * non-noise field of `view`, recursing into nested objects/arrays.
 *
 * `view` is typically the raw entity plus any derived fields a caller has
 * joined in (a resolved category/venue name, a formatted display id) —
 * genericMatch itself does no joining, it only decides whether a given
 * plain object counts as a match.
 */
export function genericMatch(
  view: Record<string, unknown> | null | undefined,
  q: string,
  depth = 3
): boolean {
  if (!view) return false;
  return Object.entries(view).some(([k, v]) => !isNoiseKey(k) && valueMatches(v, q, depth));
}
